/**
 * @multimodal/web-sdk browser runtime.
 *
 * Type declarations live in index.ts. Keep this file dependency-free so Vue 2
 * applications can consume the SDK without adding a TypeScript loader.
 */

export const PROTOCOL_VERSION = '1.0.0'

const ACTION_NAME_PATTERN = /^[a-z][a-z0-9_]*\.[a-zA-Z][a-zA-Z0-9_]*$/

function generateId(prefix = '') {
  return `${prefix}${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function resolveLocalRef(ref, rootSchema) {
  if (typeof ref !== 'string' || !ref.startsWith('#/')) return null
  return ref.slice(2).split('/').reduce((value, segment) => {
    if (value == null) return null
    const key = segment.replace(/~1/g, '/').replace(/~0/g, '~')
    return value[key]
  }, rootSchema)
}

function typeMatches(value, expected) {
  if (expected === 'null') return value === null
  if (expected === 'array') return Array.isArray(value)
  if (expected === 'object') return value !== null && typeof value === 'object' && !Array.isArray(value)
  if (expected === 'integer') return Number.isInteger(value)
  if (expected === 'number') return typeof value === 'number' && Number.isFinite(value)
  return typeof value === expected
}

function validateValue(value, schema, rootSchema, path, errors) {
  if (!schema || typeof schema !== 'object') return

  if (schema.$ref) {
    const resolved = resolveLocalRef(schema.$ref, rootSchema)
    if (!resolved) {
      errors.push(`${path}: unresolved schema reference ${schema.$ref}`)
      return
    }
    validateValue(value, resolved, rootSchema, path, errors)
    return
  }

  if (Array.isArray(schema.oneOf)) {
    const matches = schema.oneOf.filter(candidate => {
      const candidateErrors = []
      validateValue(value, candidate, rootSchema, path, candidateErrors)
      return candidateErrors.length === 0
    })
    if (matches.length !== 1) errors.push(`${path}: expected exactly one matching schema`)
    return
  }

  if (Object.prototype.hasOwnProperty.call(schema, 'const') && value !== schema.const) {
    errors.push(`${path}: expected constant ${JSON.stringify(schema.const)}`)
    return
  }
  if (Array.isArray(schema.enum) && !schema.enum.includes(value)) {
    errors.push(`${path}: expected one of ${schema.enum.join(', ')}`)
    return
  }

  const expectedTypes = Array.isArray(schema.type) ? schema.type : (schema.type ? [schema.type] : [])
  if (expectedTypes.length && !expectedTypes.some(type => typeMatches(value, type))) {
    errors.push(`${path}: expected ${expectedTypes.join(' or ')}`)
    return
  }

  if (typeof value === 'string') {
    if (schema.minLength !== undefined && value.length < schema.minLength) errors.push(`${path}: too short`)
    if (schema.maxLength !== undefined && value.length > schema.maxLength) errors.push(`${path}: too long`)
    if (schema.pattern && !(new RegExp(schema.pattern)).test(value)) errors.push(`${path}: invalid format`)
  }

  if (typeof value === 'number') {
    if (schema.minimum !== undefined && value < schema.minimum) errors.push(`${path}: must be >= ${schema.minimum}`)
    if (schema.maximum !== undefined && value > schema.maximum) errors.push(`${path}: must be <= ${schema.maximum}`)
  }

  if (Array.isArray(value)) {
    if (schema.minItems !== undefined && value.length < schema.minItems) errors.push(`${path}: too few items`)
    if (schema.maxItems !== undefined && value.length > schema.maxItems) errors.push(`${path}: too many items`)
    if (schema.items) value.forEach((item, index) => validateValue(item, schema.items, rootSchema, `${path}[${index}]`, errors))
  }

  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const properties = schema.properties || {}
    for (const required of schema.required || []) {
      if (!Object.prototype.hasOwnProperty.call(value, required)) errors.push(`${path}.${required}: required`)
    }
    for (const [key, child] of Object.entries(properties)) {
      if (Object.prototype.hasOwnProperty.call(value, key)) validateValue(value[key], child, rootSchema, `${path}.${key}`, errors)
    }
    if (schema.additionalProperties === false) {
      for (const key of Object.keys(value)) {
        if (!Object.prototype.hasOwnProperty.call(properties, key)) errors.push(`${path}.${key}: unexpected property`)
      }
    }
  }
}

/** Validate the Draft 2020-12 subset used by the frozen action contracts. */
export function validateSchema(value, schema) {
  const errors = []
  validateValue(value, schema || {}, schema || {}, '$', errors)
  return { valid: errors.length === 0, errors }
}

export class AssistantClient {
  constructor(clientId) {
    this._ws = null
    this._options = null
    this._actions = new Map()
    this._seq = 0
    this._clientId = clientId || generateId('client-')
    this._conversationId = generateId('conversation-')
    this._sessionId = ''
    this._connected = false
    this._heartbeatTimer = null
    this._reconnectTimer = null
    this._reconnectAttempts = 0
    this._stateCbs = new Set()
    this._confirmCbs = new Set()
    this._errorCbs = new Set()
    this._connectCbs = new Set()
    this._disconnectCbs = new Set()
    this._lastCloseCode = null
    this._lastCloseReason = ''
  }

  async connect(options) {
    this._options = options
    const origin = options.origin || (typeof window !== 'undefined' ? window.location.origin : 'unknown')
    const query = new URLSearchParams({
      token: options.token || '',
      origin,
      clientId: this._clientId,
      clientRole: options.clientRole || '',
    })

    return new Promise((resolve, reject) => {
      let ws
      try { ws = new WebSocket(`${options.url}?${query.toString()}`) } catch (error) { reject(error); return }
      this._ws = ws
      ws.onopen = () => {
        this._connected = true
        this._reconnectAttempts = 0
        this._sendHello(origin)
        this._startHeartbeat(options.heartbeatIntervalMs || 5000)
        this._connectCbs.forEach(callback => { try { callback() } catch (_) {} })
        resolve()
      }
      ws.onmessage = event => this._handleMessage(event.data)
      ws.onerror = () => { if (!this._connected) reject(new Error('WebSocket connection failed')) }
      ws.onclose = (event) => {
        this._lastCloseCode = event && Number.isFinite(event.code) ? event.code : null
        this._lastCloseReason = event && event.reason ? event.reason : ''
        const wasConnected = this._connected
        this._connected = false
        this._stopHeartbeat()
        if (wasConnected) this._disconnectCbs.forEach(callback => { try { callback() } catch (_) {} })
        if (options.reconnect?.enabled !== false && this._reconnectAttempts < (options.reconnect?.maxAttempts || 10)) {
          this._scheduleReconnect()
        }
      }
    })
  }

  disconnect() {
    this._stopHeartbeat()
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer)
    this._reconnectTimer = null
    if (this._ws) {
      this._ws.onclose = null
      this._ws.onerror = null
      this._ws.onmessage = null
      try { this._ws.close(1000, 'client_disconnect') } catch (_) {}
    }
    this._ws = null
    this._connected = false
    this._sessionId = ''
  }

  get isConnected() { return this._connected }
  get clientId() { return this._clientId }
  get sessionId() { return this._sessionId }
  get conversationId() { return this._conversationId }
  get lastCloseCode() { return this._lastCloseCode }
  get lastCloseReason() { return this._lastCloseReason }

  registerAction(action) {
    if (!action || !ACTION_NAME_PATTERN.test(action.name || '') || typeof action.execute !== 'function') return () => {}
    this._actions.set(action.name, action)
    this._sendActionCatalog()
    return () => this.unregisterAction(action.name)
  }

  unregisterAction(name) {
    if (this._actions.delete(name)) this._sendActionCatalog()
  }

  getRegisteredActions() { return Array.from(this._actions.keys()) }

  _validateArgs(name, args) {
    const action = this._actions.get(name)
    if (!action) return [false, [`Action "${name}" not registered`]]
    const result = validateSchema(args, action.inputSchema)
    return [result.valid, result.errors]
  }

  updateContext(context) { this._send('context.update', context) }
  updatePointerTarget(target) { this._send('pointer.update', target) }
  reportSpeechTranscript(text, isFinal = true, language = 'zh') { this._send('speech.transcript', { text, isFinal, language }) }

  reportTaskResult(taskId, result, actionName = '', correlationId = '', lifecycle = {}) {
    const output = result.output || null
    this._send('action.result', {
      taskId,
      conversationId: lifecycle.conversationId || this._conversationId,
      rootTaskId: lifecycle.rootTaskId || taskId,
      parentTaskId: lifecycle.parentTaskId || null,
      revision: lifecycle.revision || 1,
      actionName,
      success: result.success,
      summary: result.summary || '',
      renderDurationMs: result.renderDurationMs || 0,
      error: result.error || result.errorCode,
      errorCode: result.errorCode || result.error,
      output,
      undoToken: result.undoToken || output?.undoToken || null,
    }, { correlationId })
  }

  cancelTask(taskId, reason = 'user_cancelled') { this._send('task.cancel', { taskId, reason }) }
  correctTask(taskId, reason = 'user_corrected') { this._send('task.correct', { taskId, reason }) }
  undoLastAction(taskId, reason = 'user_undo') { this._send('task.undo', { taskId, reason }) }
  confirmTask(taskId, accepted) { this._send('confirmation.response', { taskId, accepted }) }

  onStateChange(callback) { this._stateCbs.add(callback); return () => this._stateCbs.delete(callback) }
  onConfirmationRequest(callback) { this._confirmCbs.add(callback); return () => this._confirmCbs.delete(callback) }
  onError(callback) { this._errorCbs.add(callback); return () => this._errorCbs.delete(callback) }
  onConnect(callback) { this._connectCbs.add(callback); return () => this._connectCbs.delete(callback) }
  onDisconnect(callback) { this._disconnectCbs.add(callback); return () => this._disconnectCbs.delete(callback) }

  _nextSeq() { return ++this._seq }

  _send(type, payload, metadata = {}) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return
    const message = {
      version: PROTOCOL_VERSION,
      type,
      requestId: metadata.requestId || generateId('req-'),
      correlationId: metadata.correlationId || '',
      conversationId: this._conversationId,
      idempotencyKey: metadata.idempotencyKey || '',
      sessionId: this._sessionId,
      clientId: this._clientId,
      seq: this._nextSeq(),
      timestamp: Date.now(),
      payload,
    }
    try { this._ws.send(JSON.stringify(message)) } catch (error) { console.error('[SDK] send failed:', error) }
  }

  _sendHello(origin) {
    this._send('client.hello', {
      clientVersion: PROTOCOL_VERSION,
      capabilities: ['context', 'actions', 'confirmation', 'undo'],
      clientRole: this._options?.clientRole || '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    })
    this._send('client.auth', { token: this._options?.token || '', origin })
  }

  _sendActionCatalog() {
    const actions = Array.from(this._actions.values()).map(action => ({
      name: action.name,
      description: action.description,
      inputSchema: action.inputSchema,
      outputSchema: action.outputSchema || {},
      riskLevel: action.riskLevel,
      timeout: action.timeout,
    }))
    this._send('action.catalog', { actions })
  }

  _startHeartbeat(intervalMs) {
    this._stopHeartbeat()
    this._heartbeatTimer = setInterval(() => this._send('heartbeat', {}), intervalMs)
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer)
    this._heartbeatTimer = null
  }

  _scheduleReconnect() {
    if (this._reconnectTimer) return
    const reconnect = this._options?.reconnect
    const delay = Math.min((reconnect?.baseDelayMs || 1000) * Math.pow(2, this._reconnectAttempts), reconnect?.maxDelayMs || 30000)
    this._reconnectAttempts++
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null
      if (this._options) this.connect(this._options).catch(() => {})
    }, delay)
  }

  _handleMessage(data) {
    let message
    try { message = JSON.parse(data) } catch (_) { return }
    if (!message || typeof message !== 'object') return
    const payload = message.payload || {}
    switch (message.type) {
      case 'client.ready':
      case 'server.hello':
      case 'connected':
        this._sessionId = payload.sessionId || message.sessionId || ''
        this._sendActionCatalog()
        break
      case 'action.invoke':
        if (typeof payload.actionName === 'string' && payload.actionName.startsWith('undo.')) {
          this._handleActionUndo({ ...payload, actionName: payload.actionName.slice(5) })
        } else {
          this._handleActionInvoke(payload, message.requestId || '')
        }
        break
      case 'task.undo':
      case 'task.undo.ack':
        this._handleActionUndo(payload)
        break
      case 'confirmation.request':
        this._confirmCbs.forEach(callback => { try { callback(payload) } catch (_) {} })
        break
      case 'task.status':
        this._stateCbs.forEach(callback => { try { callback(payload.state, payload.taskId, payload) } catch (_) {} })
        break
      case 'task.error':
      case 'error':
        this._errorCbs.forEach(callback => {
          try { callback({ errorCode: payload.errorCode, errorMessage: payload.errorMessage, taskId: payload.taskId }) } catch (_) {}
        })
        break
      default:
        break
    }
  }

  async _withTimeout(promise, timeout) {
    if (!timeout) return promise
    let timer
    try {
      return await Promise.race([
        promise,
        new Promise((_, reject) => { timer = setTimeout(() => reject(Object.assign(new Error('Action timed out'), { code: 'ACTION_TIMEOUT' })), timeout) }),
      ])
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  async _handleActionInvoke(payload, invokeRequestId = '') {
    const { taskId, actionName, arguments: args = {}, undoToken = null } = payload
    const lifecycle = {
      conversationId: payload.conversationId || this._conversationId,
      rootTaskId: payload.rootTaskId || taskId,
      parentTaskId: payload.parentTaskId || null,
      revision: payload.revision || 1,
    }
    const action = this._actions.get(actionName)
    if (!action) {
      this.reportTaskResult(taskId, { success: false, error: 'ACTION_NOT_FOUND', errorCode: 'ACTION_NOT_FOUND' }, actionName, invokeRequestId, lifecycle)
      return
    }
    const validation = validateSchema(args, action.inputSchema)
    if (!validation.valid) {
      this.reportTaskResult(taskId, {
        success: false,
        error: 'SCHEMA_INVALID',
        errorCode: 'SCHEMA_INVALID',
        summary: validation.errors.join(', '),
      }, actionName, invokeRequestId, lifecycle)
      return
    }

    const startedAt = Date.now()
    try {
      const result = await this._withTimeout(
        Promise.resolve(action.execute(args, { taskId, sessionId: this._sessionId, undoToken })),
        action.timeout,
      )
      this.reportTaskResult(taskId, { ...result, renderDurationMs: result.renderDurationMs || Date.now() - startedAt }, actionName, invokeRequestId, lifecycle)
    } catch (error) {
      const code = error?.code || 'ACTION_FAILED'
      this.reportTaskResult(taskId, { success: false, summary: error?.message || 'Execution failed', error: code, errorCode: code }, actionName, invokeRequestId, lifecycle)
    }
  }

  async _handleActionUndo(payload) {
    const action = this._actions.get(payload.actionName)
    if (!action || typeof action.undo !== 'function') {
      this.reportTaskResult(payload.taskId, { success: false, error: 'UNDO_UNAVAILABLE', errorCode: 'UNDO_UNAVAILABLE' }, payload.actionName)
      return
    }
    try {
      await action.undo({ taskId: payload.taskId, sessionId: this._sessionId, undoToken: payload.undoToken || null })
    } catch (error) {
      const code = error?.code || 'RESTORE_FAILED'
      this._errorCbs.forEach(callback => {
        try { callback({ errorCode: code, errorMessage: error?.message || 'Undo failed', taskId: payload.taskId }) } catch (_) {}
      })
    }
  }
}
